// Utility functions for Advanced Reports

/**
 * Format currency values for display
 */
export const formatCurrency = (value, options = {}) => {
  if (value === null || value === undefined) return 'N/A';
  
  const defaultOptions = {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  };
  
  return new Intl.NumberFormat('en-GB', { ...defaultOptions, ...options }).format(value);
};

/**
 * Format percentage values for display
 */
export const formatPercentage = (value, decimals = 1) => {
  if (value === null || value === undefined) return 'N/A';
  return `${value.toFixed(decimals)}%`;
};

/**
 * Format number values for display
 */
export const formatNumber = (value, options = {}) => {
  if (value === null || value === undefined) return 'N/A';
  return new Intl.NumberFormat('en-GB', options).format(value);
};

/**
 * Format date values for display
 */
export const formatDate = (value, options = {}) => {
  if (!value) return 'N/A';
  
  const defaultOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  };
  
  return new Date(value).toLocaleDateString('en-GB', { ...defaultOptions, ...options });
};

/**
 * Format datetime values for display
 */
export const formatDateTime = (value, options = {}) => {
  if (!value) return 'N/A';
  
  const defaultOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  };
  
  return new Date(value).toLocaleString('en-GB', { ...defaultOptions, ...options });
};

/**
 * Calculate percentage change between two values
 */
export const calculatePercentageChange = (current, previous) => {
  if (previous === 0) return current > 0 ? 100 : 0;
  return ((current - previous) / previous) * 100;
};

/**
 * Get color class based on performance indicator
 */
export const getPerformanceColor = (value, type = 'percentage') => {
  if (value === null || value === undefined) return 'text-gray-500';
  
  switch (type) {
    case 'percentage':
      if (value > 0) return 'text-green-600';
      if (value < 0) return 'text-red-600';
      return 'text-gray-600';
    
    case 'currency':
      if (value > 0) return 'text-green-600';
      if (value < 0) return 'text-red-600';
      return 'text-gray-600';
    
    case 'status':
      if (value === 'active' || value === 'completed') return 'text-green-600';
      if (value === 'pending' || value === 'inactive') return 'text-yellow-600';
      if (value === 'cancelled' || value === 'failed') return 'text-red-600';
      return 'text-gray-600';
    
    default:
      return 'text-gray-600';
  }
};

/**
 * Get background color class based on performance indicator
 */
export const getPerformanceBgColor = (value, type = 'percentage') => {
  if (value === null || value === undefined) return 'bg-gray-100';
  
  switch (type) {
    case 'percentage':
      if (value > 0) return 'bg-green-50';
      if (value < 0) return 'bg-red-50';
      return 'bg-gray-50';
    
    case 'currency':
      if (value > 0) return 'bg-green-50';
      if (value < 0) return 'bg-red-50';
      return 'bg-gray-50';
    
    case 'status':
      if (value === 'active' || value === 'completed') return 'bg-green-50';
      if (value === 'pending' || value === 'inactive') return 'bg-yellow-50';
      if (value === 'cancelled' || value === 'failed') return 'bg-red-50';
      return 'bg-gray-50';
    
    default:
      return 'bg-gray-50';
  }
};

/**
 * Generate date range for reports
 */
export const generateDateRange = (period = 'month') => {
  const now = new Date();
  const start = new Date();
  
  switch (period) {
    case 'today':
      start.setHours(0, 0, 0, 0);
      break;
    case 'week':
      start.setDate(now.getDate() - 7);
      break;
    case 'month':
      start.setMonth(now.getMonth() - 1);
      break;
    case 'quarter':
      start.setMonth(now.getMonth() - 3);
      break;
    case 'year':
      start.setFullYear(now.getFullYear() - 1);
      break;
    default:
      start.setMonth(now.getMonth() - 1);
  }
  
  return {
    start: start.toISOString().split('T')[0],
    end: now.toISOString().split('T')[0]
  };
};

/**
 * Debounce function for search inputs
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Sort array of objects by key
 */
export const sortBy = (array, key, direction = 'asc') => {
  return [...array].sort((a, b) => {
    const aValue = a[key];
    const bValue = b[key];
    
    if (aValue === null || aValue === undefined) return 1;
    if (bValue === null || bValue === undefined) return -1;
    
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return direction === 'asc' 
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }
    
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return direction === 'asc' ? aValue - bValue : bValue - aValue;
    }
    
    return 0;
  });
};

/**
 * Group array of objects by key
 */
export const groupBy = (array, key) => {
  return array.reduce((groups, item) => {
    const group = item[key];
    groups[group] = groups[group] || [];
    groups[group].push(item);
    return groups;
  }, {});
};

/**
 * Calculate totals for grouped data
 */
export const calculateGroupTotals = (groupedData, fields) => {
  return Object.entries(groupedData).map(([key, items]) => {
    const totals = fields.reduce((acc, field) => {
      acc[field] = items.reduce((sum, item) => sum + (item[field] || 0), 0);
      return acc;
    }, {});
    
    return {
      key,
      items,
      totals,
      count: items.length
    };
  });
};

/**
 * Export data to CSV format
 */
export const exportToCSV = (data, filename = 'export.csv') => {
  if (!data || data.length === 0) return;
  
  const headers = Object.keys(data[0]);
  const csvContent = [
    headers.join(','),
    ...data.map(row => 
      headers.map(header => {
        const value = row[header];
        return typeof value === 'string' && value.includes(',') 
          ? `"${value}"` 
          : value;
      }).join(',')
    )
  ].join('\n');
  
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Generate PDF content (placeholder for future PDF generation)
 */
export const generatePDFContent = (data, title) => {
  // This would integrate with a PDF generation library like jsPDF
  // For now, return a placeholder structure
  return {
    title,
    data,
    generatedAt: new Date().toISOString(),
    totalRecords: data.length
  };
};

/**
 * Validate email address
 */
export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Get relative time string (e.g., "2 hours ago")
 */
export const getRelativeTime = (date) => {
  const now = new Date();
  const diffInSeconds = Math.floor((now - new Date(date)) / 1000);
  
  if (diffInSeconds < 60) return 'Just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
  if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)} days ago`;
  
  return formatDate(date);
};

/**
 * Truncate text with ellipsis
 */
export const truncateText = (text, maxLength = 50) => {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

/**
 * Generate unique ID
 */
export const generateId = () => {
  return Math.random().toString(36).substr(2, 9);
};

/**
 * Deep clone object
 */
export const deepClone = (obj) => {
  if (obj === null || typeof obj !== 'object') return obj;
  if (obj instanceof Date) return new Date(obj.getTime());
  if (obj instanceof Array) return obj.map(item => deepClone(item));
  if (typeof obj === 'object') {
    const clonedObj = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        clonedObj[key] = deepClone(obj[key]);
      }
    }
    return clonedObj;
  }
};




